import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { causeCoverUrl } from "@/lib/payments";
import { useLang } from "@/contexts/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DonateModal from "@/components/DonateModal";
import { HandHeart, Calendar, Target, IndianRupee, ArrowRight } from "lucide-react";

function inr(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n));
}

function pct(raised, target) {
  if (!target || target <= 0) return 0;
  return Math.min(100, Math.round((Number(raised || 0) / Number(target)) * 100));
}

export default function Causes() {
  const { lang, t } = useLang();
  const fontH = lang === "ta" ? "font-serif-ta" : "font-serif-en";
  const fontB = lang === "ta" ? "font-sans-ta" : "";
  const [causes, setCauses] = useState([]);
  const [content, setContent] = useState(null);
  const [active, setActive] = useState(null);

  const load = async () => {
    const [c, sc] = await Promise.all([
      api.get("/causes?active_only=true").then((r) => r.data).catch(() => []),
      api.get("/site-content").then((r) => r.data).catch(() => null),
    ]);
    setCauses(c);
    setContent(sc);
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="causes-page" className="min-h-screen bg-stone-50">
      <Navbar />

      <section className="bg-emerald-950 text-stone-100 py-20 md:py-28 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-amber-700/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-emerald-800/40 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="inline-flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.3em] mb-4">
            <span className="w-8 h-px bg-amber-300" />
            <HandHeart className="w-4 h-4" /> Fundraising
          </div>
          <h1 className={`${fontH} text-4xl md:text-6xl text-amber-50 leading-tight max-w-3xl`}>
            {lang === "ta" ? "காரணங்களுக்கான நன்கொடைகள்" : "Donate to a Cause"}
          </h1>
          <p className={`mt-5 text-stone-300/90 max-w-2xl ${fontB}`}>
            {lang === "ta"
              ? "எங்கள் சமூகத்தை வளர்க்க உதவும் ஒவ்வொரு பங்களிப்பும் மதிப்புமிக்கது. குறிப்பிட்ட காரணங்களுக்கு பாதுகாப்பாக நன்கொடை அளிக்கவும்."
              : "Every contribution helps grow our community and madrasa. Donate securely toward specific causes or for general welfare."}
          </p>
          <button
            data-testid="general-donate-btn"
            onClick={() => setActive({ general: true })}
            className="btn-gold mt-8 inline-flex items-center gap-2"
          >
            <HandHeart className="w-4 h-4" /> {lang === "ta" ? "பொது நன்கொடை அளி" : "Make a General Donation"}
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {causes.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-stone-400 text-sm uppercase tracking-widest mb-2">No active causes right now</div>
            <p className="text-stone-500">Please check back later, or contribute through general donation above.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {causes.map((c, i) => {
              const title = lang === "ta" && c.title_ta ? c.title_ta : c.title_en;
              const desc = lang === "ta" && c.description_ta ? c.description_ta : c.description_en;
              const progress = pct(c.raised_amount, c.target_amount);
              return (
                <article key={c.id} data-testid={`cause-card-${i}`} className="mosque-card overflow-hidden flex flex-col">
                  <div className="relative h-44 bg-stone-100">
                    {c.cover_path ? (
                      <img src={causeCoverUrl(c.id)} alt={title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-emerald-900 to-emerald-700 grid place-items-center text-amber-300 font-serif-en text-5xl">۞</div>
                    )}
                    {c.deadline && (
                      <div className="absolute top-3 left-3 bg-white/95 text-xs px-2 py-1 rounded-md flex items-center gap-1 text-stone-700">
                        <Calendar className="w-3 h-3" /> by {new Date(c.deadline).toLocaleDateString(lang === "ta" ? "ta-IN" : "en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className={`${fontH} text-xl text-emerald-950 leading-snug`}>{title}</h3>
                    {desc && <p className={`text-sm text-stone-600 mt-2 leading-relaxed line-clamp-3 ${fontB}`}>{desc}</p>}

                    {c.target_amount > 0 && (
                      <div className="mt-4">
                        <div className="flex items-end justify-between text-sm mb-1.5">
                          <span className="text-emerald-900 font-medium flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" />{inr(c.raised_amount)}</span>
                          <span className="text-stone-500 text-xs flex items-center gap-1"><Target className="w-3 h-3" />₹{inr(c.target_amount)}</span>
                        </div>
                        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-emerald-700 to-amber-500 transition-all" style={{ width: `${progress}%` }} />
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-1">{progress}% raised</div>
                      </div>
                    )}

                    <button
                      data-testid={`donate-cause-${i}`}
                      onClick={() => setActive(c)}
                      className="btn-primary mt-5 inline-flex items-center justify-center gap-2 w-full"
                    >
                      <HandHeart className="w-4 h-4" /> Donate Now <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <Footer content={content} />

      <DonateModal
        cause={active && !active.general ? active : null}
        content={content}
        open={!!active}
        onClose={() => setActive(null)}
        onSuccess={load}
      />
    </div>
  );
}
